import React, { useState, useEffect } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { Typography, Grid, Pagination, Box, Skeleton, useTheme, useMediaQuery } from '@mui/material';
import WikipediaViewer from '../../components/WikipediaViewer';
import Expandable from '../../components/common/Expandable';
import { getAnimeByGenre, getAnimeByStudio, getAnimeByProducer, getBasicAnimeStatuses } from '../../services/animeApi';
import { Anime } from '../../types/Anime';
import LoadingPlaceholder from '../../components/LoadingPlaceholder';
import AnimeCard from '../../components/AnimeCard';
import { Helmet } from 'react-helmet-async';
import AdvertisementResponsive from '../../components/advertisement/Ad_GA_res';
import Advertisement from '../../components/advertisement/Ad_wide_728';
import RectangleAdvertisement from '../../components/advertisement/Ad_rectangle_300';

const ITEMS_PER_PAGE = 30;

const AnimeListByCategory: React.FC = () => {
  const { type, id } = useParams<{ type: string; id: string }>();
  const [animes, setAnimes] = useState<Anime[]>([]);
  const [loading, setLoading] = useState(true);
  const [japaneseName, setJapaneseName] = useState<string | null>(null);
  const [englishName, setEnglishName] = useState('');
  const [totalPages, setTotalPages] = useState(0);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [animeStatuses, setAnimeStatuses] = useState<{ [key: number]: number | null }>({});
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [wikiSummary, setWikiSummary] = useState<string | null>(null);
  const [wikiOpen, setWikiOpen] = useState(false);
  const [hasWikiData, setHasWikiData] = useState(false);
  const [wikiLoading, setWikiLoading] = useState(true);
  const location = useLocation();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  useEffect(() => {
    const token = localStorage.getItem('token');
    setIsLoggedIn(!!token);
  }, []);

  useEffect(() => {
    const fetchStatuses = async () => {
      if (!isLoggedIn || animes.length === 0) return;
      const malIds = animes.map(a => a.mal_id);
      const statuses = await getBasicAnimeStatuses(malIds);
      setAnimeStatuses(statuses);
    };
    fetchStatuses();
  }, [animes, isLoggedIn]);

  const queryParams = new URLSearchParams(location.search);
  const currentPage = parseInt(queryParams.get('page') || '1', 10);

  useEffect(() => {
    const fetchAnimes = async () => {
      setLoading(true);
      try {
        if (!id) throw new Error('Invalid ID');
        let response;
        switch (type) {
          case 'genre':
            response = await getAnimeByGenre(id, currentPage);
            setJapaneseName(response.genre_name_ja || null);
            setEnglishName(response.genre_name);
            break;
          case 'studio':
            response = await getAnimeByStudio(id, currentPage);
            setJapaneseName(response.studio_name_ja || null);
            setEnglishName(response.studio_name);
            break;
          case 'producer':
            response = await getAnimeByProducer(id, currentPage);
            setJapaneseName(response.producer_name_ja || null);
            setEnglishName(response.producer_name);
            break;
          default:
            throw new Error('Invalid category type');
        }
        setAnimes(response.results || []);
        setTotalCount(response.count || 0);
        setTotalPages(Math.ceil((response.count || 0) / ITEMS_PER_PAGE));
      } catch (error) {
        console.error('アニメ一覧の取得に失敗しました', error);
        setAnimes([]);
      } finally {
        setLoading(false);
      }
    };
    fetchAnimes();
  }, [id, type, currentPage]);

  useEffect(() => {
    const searchName = japaneseName || englishName;
    if (!searchName) return;
    setWikiLoading(true);
    const fetchWikiSummary = async () => {
      try {
        const cachedData = localStorage.getItem(`wiki_summary_${searchName}`);
        if (cachedData) {
          const { summary, timestamp } = JSON.parse(cachedData);
          if (new Date().getTime() - timestamp < 24 * 60 * 60 * 1000) {
            setWikiSummary(summary);
            setHasWikiData(true);
            setWikiLoading(false);
            return;
          }
        }
        const response = await fetch(`https://ja.wikipedia.org/api/rest_v1/page/summary/${encodeURIComponent(searchName)}`);
        const data = await response.json();
        if (data.extract) {
          setWikiSummary(data.extract);
          setHasWikiData(true);
          localStorage.setItem(`wiki_summary_${searchName}`, JSON.stringify({
            summary: data.extract,
            timestamp: new Date().getTime()
          }));
        } else {
          setHasWikiData(false);
        }
      } catch (error) {
        console.error('Wikipedia APIからのデータ取得に失敗しました:', error);
        setHasWikiData(false);
      } finally {
        setWikiLoading(false);
      }
    };
    fetchWikiSummary();
  }, [japaneseName, englishName]);

  const handlePageChange = (_event: React.ChangeEvent<unknown>, value: number) => {
    navigate(`/anime/${type}/${id}?page=${value}`);
  };

  const displayName = japaneseName || englishName;

  return (
    <>
      <Helmet>
        <title>{`${displayName} のアニメ一覧 | PULSE データベース`}</title>
      </Helmet>
      <Typography variant="h5" component="h1" gutterBottom>
        {displayName}
        {japaneseName && englishName && (
          <Typography
            component="span"
            sx={{
              ml: 1,
              fontSize: '0.8rem',
              color: 'text.secondary',
              fontWeight: 'normal',
              opacity: 0.8,
              verticalAlign: 'middle',
            }}
          >
            {englishName}
          </Typography>
        )}
        {type === 'producer' && (
          <Typography
            component="span"
            sx={{
              ml: 0.5,
              fontSize: '0.7rem',
              color: 'text.secondary',
              opacity: 0.7,
              fontWeight: 'normal',
              verticalAlign: 'middle',
            }}
          >
            （製作）
          </Typography>
        )}
        <Typography
          component="span"
          sx={{
            ml: 1,
            fontSize: '0.8rem',
            color: 'text.secondary',
            opacity: 0.7,
            fontWeight: 'normal',
            verticalAlign: 'middle',
          }}
        >
          {totalCount > 0 && `• ${totalCount}作品`}
        </Typography>
      </Typography>

      <Grid container>
        <Grid item xs={12} md={8}>
          <Box sx={{ position: 'relative', mb: 3, minHeight: 60 }}>
            {wikiLoading ? (
              <>
                <Skeleton variant="text" width="100%" height={20} />
                <Skeleton variant="text" width="80%" height={20} />
              </>
            ) : wikiSummary ? (
              <Expandable maxLines={3} expandButtonStyle="icon" iconSize={18} iconPadding={4} iconColor="#ffffff" iconBgColor="rgba(0, 0, 0, 0.9)">
                <Typography
                  variant="body2"
                  paragraph
                  sx={{
                    color: 'text.secondary',
                    fontSize: '0.7rem',
                    mb: 0
                  }}
                >
                  {wikiSummary}
                  <Typography
                    variant="body2"
                    component="span"
                    sx={{
                      color: 'primary.main',
                      cursor: 'pointer',
                      ml: 1,
                      fontSize: 'inherit',
                      '&:hover': { textDecoration: 'underline' }
                    }}
                    onClick={() => setWikiOpen(true)}
                  >
                    詳細を見る
                  </Typography>
                </Typography>
              </Expandable>
            ) : (
              <Typography
                variant="body2"
                paragraph
                sx={{
                  color: 'text.secondary',
                  fontSize: '0.7rem',
                  mb: 0,
                  opacity: 0.5
                }}
              >
                概要情報なし
              </Typography>
            )}
          </Box>
        </Grid>
      </Grid>

      {loading ? (
        <LoadingPlaceholder type="card" count={ITEMS_PER_PAGE} />
      ) : (
        <>
          <Grid container spacing={{ xs: 1, sm: 1.5 }}>
            {animes.map((anime, index) => (
              <React.Fragment key={anime.mal_id}>
                <Grid item xs={4} sm={3} md={2}>
                  <AnimeCard
                    anime={anime}
                    initialStatus={animeStatuses[anime.mal_id]}
                  />
                </Grid>
                {isMobile && index === 11 && (
                  <Grid item xs={12}>
                    <AdvertisementResponsive />
                  </Grid>
                )}
              </React.Fragment>
            ))}
          </Grid>

          {isMobile ? <RectangleAdvertisement /> : <Advertisement />}

          {totalPages > 1 && (
            <Pagination
              count={totalPages}
              page={currentPage}
              onChange={handlePageChange}
              sx={{ mt: 4, display: 'flex', justifyContent: 'center' }}
            />
          )}
        </>
      )}

      <WikipediaViewer
        open={wikiOpen}
        onClose={() => setWikiOpen(false)}
        title={japaneseName || englishName}
      />
    </>
  );
};

export default AnimeListByCategory;
